/**
 * Weapon Mount Calculations for Traveller Ship Builder
 * Implements Traveller rules for Hardpoints and Firmpoints
 */

/**
 * Calculate the number of Firmpoints for ships under 100 tons
 * According to Traveller rules:
 * - 1-34 tons: 1 Firmpoint
 * - 35-69 tons: 2 Firmpoints  
 * - 70-99 tons: 3 Firmpoints
 */
export const calculateFirmpoints = (shipSize: number): number => {
  if (shipSize >= 100) {
    return 0; // Ships 100+ tons use Hardpoints, not Firmpoints
  }
  
  if (shipSize < 35) {
    return 1;
  } else if (shipSize < 70) {
    return 2;
  } else {
    return 3;
  }
};

/**
 * Calculate the number of Hardpoints for ships 100+ tons
 * According to Traveller rules: 1 Hardpoint per 100 tons
 */
export const calculateHardpoints = (shipSize: number): number => {
  if (shipSize < 100) {
    return 0; // Ships under 100 tons use Firmpoints, not Hardpoints
  }
  
  return Math.floor(shipSize / 100);
};

/**
 * Check if a ship uses Firmpoints (under 100 tons) or Hardpoints (100+ tons)
 */
export const usesFirmpoints = (shipSize: number): boolean => {
  return shipSize < 100;
};

/**
 * Calculate weapon range limitations for weapons mounted on Firmpoints
 * According to Traveller rules:
 * - Weapons of Medium range or less are reduced to Adjacent range
 * - Weapons of Long or greater are reduced to Close range
 * - A weapon on a Firmpoint may not have its range increased beyond Close by any means
 * - Firmpoint range limitations do not apply to missiles and torpedoes
 */
export const getFirmpointRangeLimit = (
  originalRange: string,
  weaponTraits: string[]
): string => {
  // Firmpoint range limitations do not apply to missiles and torpedoes
  if (weaponTraits.includes("Missile") || weaponTraits.includes("Torpedo")) {
    return originalRange;
  }
  
  // Apply range limitations
  switch (originalRange) {
    case "Adjacent":
    case "Close":
    case "Short":
    case "Medium":
      return "Adjacent";
    case "Long":
    case "Very Long":
    case "Extreme":
      return "Close";
    default:
      return originalRange;
  }
};

/**
 * Calculate power reduction for weapons mounted on Firmpoints
 * According to Traveller rules: Power requirements of the weapon are reduced by 25% (rounding up)
 */
export const calculateFirmpointPowerReduction = (originalPower: number): number => {
  const reduction = originalPower * 0.25;
  return Math.ceil(originalPower - reduction);
};

/**
 * Calculate Firmpoint consumption for Barbettes on small craft
 * According to Traveller rules: Barbettes consume three Firmpoints
 */
export const getBarbetteFirmpointConsumption = (shipSize: number): number => {
  if (shipSize >= 100) {
    return 1; // Ships 100+ tons use Hardpoints, Barbettes consume 1 Hardpoint
  }
  return 3; // Small craft use Firmpoints, Barbettes consume 3 Firmpoints
};

/**
 * Check if a ship can upgrade one Firmpoint to a turret
 * According to Traveller rules: One (and only one) Firmpoint can be upgraded to a single (not double or triple) turret
 */
export const canUpgradeFirmpointToTurret = (
  shipSize: number,
  existingTurretUpgrades: number
): boolean => {
  if (shipSize >= 100) {
    return false; // Only applies to ships under 100 tons (using Firmpoints)
  }
  
  return existingTurretUpgrades === 0; // Only one Firmpoint can be upgraded
};

/**
 * Check if a specific mount can be upgraded from Firmpoint to turret
 */
export const canUpgradeMountToTurret = (
  mountType: string,
  shipSize: number,
  existingTurretUpgrades: number
): boolean => {
  if (!canUpgradeFirmpointToTurret(shipSize, existingTurretUpgrades)) {
    return false;
  }
  
  return mountType === "fixed"; // Only fixed mounts (Firmpoints) can be upgraded
};

/**
 * Get mount configuration for different mount types
 * Includes proper Firmpoint/Hardpoint consumption and costs
 */
export const getMountConfig = (mountType: string, turretType?: string, shipSize: number = 100) => {
  const usesFirmpointsForShip = usesFirmpoints(shipSize);
  
  switch (mountType) {
    case "fixed":
      return { 
        hardpointsUsed: 1, 
        firmpointsUsed: 1, 
        tons: 0, 
        cost: 100000, // MCr0.1
        maxWeapons: 3,
        isFirmpoint: usesFirmpointsForShip
      };
    case "turret":
      const maxWeapons =
        turretType === "single" ? 1 : turretType === "double" ? 2 : 3;
      const turretCost = 
        turretType === "single" ? 200000 : // MCr0.2
        turretType === "double" ? 500000 : // MCr0.5
        1000000; // MCr1 for triple
      return { 
        hardpointsUsed: 1, 
        firmpointsUsed: 1, // Turrets on small craft consume 1 Firmpoint
        tons: 1, 
        cost: turretCost, 
        maxWeapons,
        isFirmpoint: usesFirmpointsForShip
      };
    case "barbette":
      const firmpointConsumption = getBarbetteFirmpointConsumption(shipSize);
      return { 
        hardpointsUsed: 1, 
        firmpointsUsed: firmpointConsumption,
        tons: 5, 
        cost: 0, // Cost calculated based on weapon
        maxWeapons: 1,
        isFirmpoint: usesFirmpointsForShip
      };
    case "smallBay":
      return { 
        hardpointsUsed: 1, 
        firmpointsUsed: 1,
        tons: 50, 
        cost: 0, 
        maxWeapons: 1,
        isFirmpoint: usesFirmpointsForShip
      };
    case "mediumBay":
      return { 
        hardpointsUsed: 1, 
        firmpointsUsed: 1,
        tons: 100, 
        cost: 0, 
        maxWeapons: 1,
        isFirmpoint: usesFirmpointsForShip
      };
    case "largeBay":
      return { 
        hardpointsUsed: 5, 
        firmpointsUsed: 5, // Large bays on small craft would consume 5 Firmpoints
        tons: 500, 
        cost: 0, 
        maxWeapons: 1,
        isFirmpoint: usesFirmpointsForShip
      };
    case "spinalMount":
      // Spinal weapons use hardpoints equal to their tonnage divided by 100, rounding up
      return { 
        hardpointsUsed: 0, // Calculated dynamically
        firmpointsUsed: 0, // Spinal mounts not available on small craft
        tons: 0, // Calculated dynamically
        cost: 0, // Calculated dynamically
        maxWeapons: 1,
        isFirmpoint: false // Spinal mounts only available on ships 100+ tons
      };
    case "pointDefenseBattery":
      return { 
        hardpointsUsed: 1, // Point defence batteries consume 1 hardpoint
        firmpointsUsed: 0, // Point defence batteries don't use firmpoints
        tons: 0, // Calculated based on selected point defence weapon
        cost: 0, // Calculated based on selected point defence weapon
        maxWeapons: 1, // Only one point defence weapon per battery
        isFirmpoint: false
      };
    case "screen":
      return { 
        hardpointsUsed: 0, // Screens don't use hardpoints
        firmpointsUsed: 0, // Screens don't use firmpoints
        tons: 0, // Calculated based on selected screen system
        cost: 0, // Calculated based on selected screen system
        maxWeapons: 1, // Only one screen system per mount
        isFirmpoint: false
      };
    default:
      return { 
        hardpointsUsed: 1, 
        firmpointsUsed: 1,
        tons: 1, 
        cost: 100000, 
        maxWeapons: 1,
        isFirmpoint: usesFirmpointsForShip
      };
  }
};
